import { useQuery } from '@tanstack/react-query';

import { readCurrentUser } from '@/lib/api/generated/api';
import type { CurrentUserView } from '@/lib/api/generated/model';
import { expectStatus } from '@/lib/api/client';

import { identityKeys } from '../queryKeys';

/**
 * The signed-in person, and what they may administer.
 *
 * Read once and cached for the session: identity does not change while someone is
 * looking at a page, and every conditional surface in the shell consults it. Without the
 * long stale time each gated entry would trigger its own request on every navigation.
 *
 * `is_org_admin` is computed by the service rather than derived here from `roles`. That
 * is deliberate: the realm role's *name* is a string shared with Keycloak's realm
 * definition and the seeding script, and a fourth place to spell it — in the client —
 * would be a fourth place to get it wrong, failing closed and invisibly (spec 072
 * FR-009).
 */
export function useCurrentUser() {
  return useQuery<CurrentUserView>({
    queryKey: identityKeys.me(),
    queryFn: async ({ signal }) => {
      const response = await readCurrentUser({ signal });
      return expectStatus(
        response,
        200,
        'Could not read the signed-in account.',
      );
    },
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
  });
}

/**
 * Whether this person administers the organization.
 *
 * Defaults to `false` while loading and on failure — an administrative surface that
 * flashes into view before the answer arrives, or appears because a request failed, is
 * worse than one that appears a moment late. The service refuses regardless; this only
 * decides whether the door is drawn.
 */
export function useIsOrgAdmin(): boolean {
  const { data } = useCurrentUser();
  return data?.is_org_admin ?? false;
}
